<template>
  <div class="mission-detail" :class="modal_name ? 'mission-detail--modal' : 'mission-detail--page'">
    <button v-if="!modal_name" class="uk-modal-close-default" type="button" uk-close @click="closeModal"></button>

    <div class="mission-detail__container">
      <div class="mission-detail__inner">
        <div v-if="modal_title" class="section-header border-0 uk-flex-middle mission-detail__header">
          <div class="section-header-left d-flex align-items-center">
            <p class="lead text-left mission-detail__title">{{ modal_title }}</p>
          </div>
        </div>

        <div v-if="errorMessages.length" class="uk-alert-danger" uk-alert>
          <template v-for="(error, index) in errorMessages">
            <span v-if="error != null" :key="index">
              <a class="uk-alert-close" uk-close></a>
              <p class="m-0" style="color: red">
                <i class="uil-exclamation-triangle"></i> {{ error }}
              </p>
            </span>
          </template>
        </div>

        <div v-if="successMessage" class="uk-alert-success" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: green">
            <i class="uil-exclamation-triangle"></i> {{ successMessage }}
          </p>
        </div>

        <!-- Görev dosyası: preview ve kullanıcı atama detayında görüntüle/indir -->
        <div v-if="mission?.mission_file" class="mission-preview">
          <div class="mission-preview__card" :class="{ 'mission-preview__card--compact': !preview }">
            <div v-if="preview && previewMetaItems.length" class="mission-preview__meta mission-preview__meta--top">
              <div
                v-for="item in previewMetaItems"
                :key="item.key"
                class="mission-preview__meta-item"
                :class="{ 'mission-preview__meta-item--full': item.fullWidth }"
              >
                <span class="mission-preview__meta-label">{{ item.label }}</span>
                <span class="mission-preview__meta-value">{{ item.value }}</span>
              </div>
            </div>

            <div class="mission-preview__section mission-preview__section--file">
              <div class="mission-preview__heading">
                <span class="mission-preview__heading-icon" aria-hidden="true">
                  <i class="uil-file"></i>
                </span>
                <h5 class="mission-preview__label">{{ $t('general.mission_file') }}</h5>
                <div class="mission-preview__actions">
                  <button
                    type="button"
                    class="mission-preview__action mission-preview__action--download"
                    @click="openMissionFile"
                  >
                    <i class="uil-external-link-alt"></i>
                    <span>{{ $t('general.view_or_download') || 'Görüntüle / İndir' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview-only: dosya yoksa meta kart -->
        <div v-else-if="preview" class="mission-preview">
          <div class="mission-preview__card">
            <div v-if="previewMetaItems.length" class="mission-preview__meta">
              <div
                v-for="item in previewMetaItems"
                :key="item.key"
                class="mission-preview__meta-item"
                :class="{ 'mission-preview__meta-item--full': item.fullWidth }"
              >
                <span class="mission-preview__meta-label">{{ item.label }}</span>
                <span class="mission-preview__meta-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Normal mode: kullanıcı cevap formu -->
        <template v-if="!preview">
          <div class="mission-detail__answer">
            <label class="mission-detail__answer-label">
              {{ mission?.mission_description }}
              <textarea
                v-if="answer_type === 'text'"
                id="short_description"
                v-model="userAnswer.answer_text"
                class="form-control mission-detail__textarea"
                name="short_description"
              />
            </label>
          </div>

          <div v-if="answer_type === 'file'" class="mission-detail__upload">
            <label for="uploadFile" class="btn btn-info mission-detail__upload-btn">
              <i class="uil-upload"></i> Dosya yükle
            </label>
            <strong class="mission-detail__or">{{ $t("general.or") }}</strong>
            <span class="dragBox mission-detail__dragbox">
              <i :class="selected_content_type.icon"></i>
              Sürükle
              <input
                id="uploadFile"
                type="file"
                draggable="true"
                @change="droppedMission"
                @dragenter="drag"
                @dragleave="drop"
                @dragover.prevent
              />
            </span>
          </div>
          <div id="previewMission" class="mission-detail__answer-preview"></div>
        </template>

        <div class="mission-detail__footer card-footer">
          <button
            v-if="modal_name"
            class="btn btn-sm btn-secondary button btn-pill cursor-pointer"
            type="button"
            @click="closeModal"
          >
            {{ $t("general.close") }}
          </button>
          <button
            v-if="!hideSubmit && !preview"
            class="btn btn-sm btn-primary button btn-pill cursor-pointer"
            type="button"
            @click="onSubmit"
          >
            <i class="uil-check"></i> {{ $t("general.submit") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Template from "../../company/Mail/Template.vue";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";
import { showSuccess, showError } from "@/utils/alertUtils";

export default {
  components: { Template },
  name: "Mission",
  props: {
    mission: {
      required: true,
      default: () => ({}),
    },
    modal_name: {
      required: true,
    },
    modal_title: {
      required: false,
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorMessages: [],
      successMessage: null,
      hideSubmit: false,
      answer_type: "",
      userAnswer: {
        mission_file: null,
        answer_text: null,
      },
      acceptable_file_type: "*",
      selected_content_type: [],
    };
  },
  computed: {
    answerTypeLabel() {
      const type = this.mission?.answer_type || this.answer_type;
      if (type === "file") return this.$t("general.upload_file") || "Dosya Yükleme";
      if (type === "text") return this.$t("general.text") || "Metin";
      return type || "-";
    },
    completionRuleLabel() {
      const rule = this.mission?.completion_rule;
      if (!rule) return "-";
      // rules => answered, admin_approved, correct_answer_given
      return this.$t(`share_management.content.${rule}`);
    },
    previewMetaItems() {
      if (!this.preview || !this.mission) return [];

      const items = [
        {
          key: "mission_description",
          label: this.$t("share_management.content.task_description"),
          value: this.mission.mission_description || "-",
          fullWidth: true,
        },
        {
          key: "answer_type",
          label: this.$t("share_management.content.answer_type"),
          value: this.answerTypeLabel,
        },
      ];

      if (this.mission.completion_rule) {
        items.push({
          key: "completion_rule",
          label: this.$t("share_management.content.completion_rule"),
          value: this.completionRuleLabel,
        });
      }

      items.push({
        key: "should_upload_mission_file",
        label: this.$t("general.load_task_file"),
        value: this.mission.should_upload_mission_file
          ? this.$t("general.yes")
          : this.$t("general.no"),
      });

      if (this.mission.accepted_right_answer) {
        items.push({
          key: "accepted_right_answer",
          label: this.$t("share_management.content.right_answer"),
          value: this.mission.accepted_right_answer,
        });
      }

      if (this.mission.created_at) {
        items.push({
          key: "created_at",
          label: this.$t("general.created_at"),
          value: this.formatMissionDate(this.mission.created_at),
        });
      }

      if (this.mission.updated_at) {
        items.push({
          key: "updated_at",
          label: this.$t("general.updated_date") || "Güncellenme",
          value: this.formatMissionDate(this.mission.updated_at),
        });
      }

      return items;
    },
  },
  methods: {
    formatMissionDate(date) {
      if (!date) return "-";
      const parsed = moment.utc(date);
      if (!parsed.isValid()) return date;
      return parsed.local().format("DD/MM/YYYY HH:mm");
    },
    // Web: tarayıcı görüntüler/indirir.
    // Native (iOS/Android WebView): window.open güvenilir değil; DOWNLOAD_FILE bridge kullanılır.
    openMissionFile() {
      const fileUrl = this.mission?.mission_file;
      if (!fileUrl) return;

      if (bridge.isNative) {
        let fileName = "mission-file";
        let extension = "";

        try {
          const pathname = new URL(fileUrl, window.location.origin).pathname;
          const rawName = decodeURIComponent(pathname.split("/").pop() || "");
          if (rawName) {
            fileName = rawName;
            const match = rawName.match(/\.([a-z0-9]+)$/i);
            if (match) extension = match[1].toLowerCase();
          }
        } catch (e) {
          // ignore
        }

        const mimeByExtension = {
          pdf: "application/pdf",
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          webp: "image/webp",
          mp4: "video/mp4",
          webm: "video/webm",
          mp3: "audio/mpeg",
          wav: "audio/wav",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ppt: "application/vnd.ms-powerpoint",
          pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          xls: "application/vnd.ms-excel",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          txt: "text/plain",
          json: "application/json",
          csv: "text/csv",
          zip: "application/zip",
        };

        native.downloadFile(
          fileUrl,
          fileName,
          mimeByExtension[extension] || "application/octet-stream"
        );
        return;
      }

      window.open(fileUrl, "_blank", "noopener,noreferrer");
    },
    dragNdrop(event) {
      let file = event.target.files[0];
      this.userAnswer.mission_file = file;
    },
    droppedMission(event) {
      let file = event.target.files[0];
      if (!file) return false;

      this.userAnswer.mission_file = event.target.files[0];
      let fileName = URL.createObjectURL(event.target.files[0]);
      let preview = document.getElementById("previewMission");
      let previewImg = false;
      if (this.acceptable_file_type.includes("pdf"))
        previewImg = document.createElement("embed");
      if (this.acceptable_file_type.includes("image"))
        previewImg = document.createElement("img");
      if (
        file.type.includes("audio") &&
        this.acceptable_file_type.includes("audio")
      )
        previewImg = document.createElement("audio");
      if (
        file.type.includes("video") &&
        this.acceptable_file_type.includes("video")
      )
        previewImg = document.createElement("video");
      if (previewImg && previewImg.controls != undefined)
        previewImg.controls = true;
      let docInfo = document.createElement("p");
      docInfo.innerHTML = this.docInfo(file);
      if (previewImg) previewImg.setAttribute("src", fileName);
      if (previewImg && previewImg.tagName.toUpperCase() == "EMBED") {
        previewImg.style.width = "100%";
        previewImg.style.minHeight = "800px";
        previewImg.setAttribute("type", file.type);
      }
      preview.innerHTML = "";
      preview.appendChild(docInfo);
      if (previewImg) preview.appendChild(previewImg);
      this.drop(event);
    },
    drag(event) {
      event.target.parentNode.className = "draging dragBox";
    },
    drop(event) {
      event.target.parentNode.className = "dragBox";
    },
    docInfo(file) {
      let file_complete_name = file.name;
      let file_name_split = file_complete_name.split(".");
      let file_extension = file_name_split.pop().toLowerCase();
      let file_name = file_name_split.join(".");
      let file_size = this.bytesToSize(file.size);
      return (
        '<div class="file-info"><div>Format: <span>' +
        file_extension.toUpperCase() +
        "</span></div><div>Doküman Adı: <span>" +
        file_name +
        "</span></div><div>Boyut: <span>" +
        file_size +
        "</span></div></div>"
      );
    },
    onSubmit() {
      const self = this;
      const formData = new FormData();
      this.userAnswer.mission_file
        ? formData.append("answer_file", this.userAnswer.mission_file)
        : formData.append("answer_text", this.userAnswer.answer_text);

      this.$emit("onSubmit", { userAnswer: formData }, (data) => {
        if (!data?.status) {
          self.resetMessages();

          if (data?.code === 422 && data?.error?.errors) {
            self.showErrors(data.error.errors);
            Object.values(data.error.errors).forEach((error) => {
              const messages = Array.isArray(error) ? error : [error];
              messages.forEach((text) => showError(text));
            });
            return;
          }

          const messageKey =
            data?.error?.message ||
            data?.error?.error ||
            "errors.error_occurred";
          const message = self.$t(messageKey);
          self.errorMessages.push(message);
          showError(message);
          return;
        }

        showSuccess(self.$t("general.saved_successfully") || self.$t("general.success"));
        setTimeout(() => {
          self.closeModal();
        }, 1000);
      });
    },
    showErrors(errors) {
      this.resetMessages();
      for (const error of Object.values(errors || {})) {
        if (Array.isArray(error)) {
          error.forEach((text) => {
            this.errorMessages.push(text);
          });
        } else {
          this.errorMessages.push(error);
        }
      }
    },
    closeModal() {
      this.$emit("onClose", true);
      if (this.modal_name) this.$modal.hide(this.modal_name);
    },
    resetMessages() {
      this.errorMessages = [];
    },
  },
  watch: {
    errorMessages() {
      this.debouncedResetValues();
    },
    mission: {
      deep: true,
      immediate: true,
      handler(val) {
        this.answer_type = val?.answer_type || "";
      },
    },
  },
  mounted() {
    this.answer_type = this.mission?.answer_type || "";
    if (this.preview) {
      this.hideSubmit = true;
    }
  },
  created: function () {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2500);
  },
};
</script>

<style scoped>
.line-height-1 {
  line-height: 1rem;
}

.mission-detail {
  width: 100%;
}

.mission-detail--modal {
  padding: 16px;
}

.mission-detail--page {
  padding: 20px 16px;
}

.mission-detail__container {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}

.mission-detail__inner {
  width: 100%;
}

.mission-detail__header {
  margin-bottom: 12px;
}

.mission-detail__title {
  margin: 0;
  font-size: 18px;
  color: #3e416d;
}

.mission-detail__answer {
  margin-bottom: 16px;
}

.mission-detail__answer-label {
  display: block;
  color: #495057;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-word;
}

.mission-detail__textarea {
  margin-top: 12px;
  min-height: 110px;
}

.mission-detail__upload {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 16px;
}

.mission-detail__upload-btn {
  width: 100%;
  justify-content: center;
}

.mission-detail__or {
  text-align: center;
  color: #9799b1;
  font-size: 13px;
}

.mission-detail__dragbox {
  width: 100%;
  min-height: 96px;
}

.mission-detail__answer-preview {
  margin-bottom: 12px;
}

.mission-detail__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 0 0;
  background: transparent;
  border: 0;
}

.mission-preview {
  margin: 0 auto 8px;
}

.mission-preview__card {
  padding: 20px;
  background: #fff;
  border: 1px solid rgba(62, 65, 109, 0.12);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(62, 65, 109, 0.06);
}

.mission-preview__card--compact {
  margin-bottom: 16px;
  padding: 16px;
  box-shadow: 0 4px 14px rgba(62, 65, 109, 0.05);
}

.mission-preview__section {
  margin-bottom: 20px;
}

.mission-preview__section--file {
  margin-bottom: 0;
}

.mission-preview__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.mission-preview__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.mission-preview__action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(62, 65, 109, 0.14);
  background: rgba(62, 65, 109, 0.04);
  color: #3e416d;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
}

.mission-preview__action:hover:not(:disabled) {
  background: rgba(62, 65, 109, 0.08);
  border-color: rgba(62, 65, 109, 0.24);
}

.mission-preview__action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.mission-preview__action--download {
  background: #3e416d;
  border-color: #3e416d;
  color: #fff;
}

.mission-preview__action--download:hover:not(:disabled) {
  background: #323557;
  border-color: #323557;
  color: #fff;
}

.mission-preview__heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(62, 65, 109, 0.08);
  color: #3e416d;
  flex-shrink: 0;
}

.mission-preview__heading-icon i {
  font-size: 15px;
}

.mission-preview__label {
  margin: 0;
  color: #3e416d;
  font-size: 14px;
  font-weight: 700;
}

.mission-preview__description {
  margin: 0;
  padding: 14px 16px;
  color: #495057;
  font-size: 15px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8f9fb;
  border: 1px solid #eef0f4;
  border-radius: 12px;
}

.mission-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #eef0f4;
}

.mission-preview__meta--top {
  padding-top: 0;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-top: 0;
  border-bottom: 1px solid #eef0f4;
}

.mission-preview__meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 150px;
  flex: 1 1 150px;
  padding: 10px 12px;
  background: rgba(62, 65, 109, 0.04);
  border: 1px solid rgba(62, 65, 109, 0.08);
  border-radius: 10px;
}

.mission-preview__meta-item--full {
  flex: 1 1 100%;
  min-width: 100%;
}

.mission-preview__meta-label {
  color: #9799b1;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.mission-preview__meta-label::after {
  content: " :";
}

.mission-preview__meta-value {
  color: #3e416d;
  font-size: 13px;
  font-weight: 600;
  word-break: break-word;
  white-space: pre-wrap;
}

@media (max-width: 767px) {
  .mission-detail--modal,
  .mission-detail--page {
    padding: 12px;
  }

  .mission-detail__title {
    font-size: 16px;
  }

  .mission-preview__card,
  .mission-preview__card--compact {
    padding: 14px;
    border-radius: 12px;
  }

  .mission-preview__heading {
    gap: 8px;
  }

  .mission-preview__label {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
  }

  .mission-preview__actions {
    width: 100%;
    margin-left: 0;
    order: 3;
  }

  .mission-preview__action {
    flex: 1 1 auto;
    justify-content: center;
    min-height: 36px;
  }

  .mission-preview__description {
    padding: 12px;
    font-size: 14px;
  }

  .mission-preview__meta-item {
    min-width: 100%;
  }

  .mission-detail__footer {
    flex-direction: column-reverse;
  }

  .mission-detail__footer .btn {
    width: 100%;
  }
}
</style>
